import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { ApprovalGate } from '../components/Approval.tsx';
import { Icon } from '../components/Icon.tsx';
import { t } from '../i18n/index.ts';
import { ApiError } from './api.ts';
import type { ApprovalBlock } from './approvals.ts';

type Theme = 'system' | 'light' | 'dark';
interface Toast {
  id: number;
  tone: 'ok' | 'danger' | 'info';
  text: string;
}
interface UiState {
  theme: Theme;
  setTheme: (t: Theme) => void;
  toast: (text: string, tone?: Toast['tone']) => void;
  /** Shows an error from the server in plain words; a step waiting for approval opens the approval dialog. */
  fail: (err: unknown) => void;
}

const Ctx = createContext<UiState | null>(null);

export function UiProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    try {
      return (localStorage.getItem('erp.theme') as Theme) || 'system';
    } catch {
      return 'system';
    }
  });
  const [toasts, setToasts] = useState<Toast[]>([]);
  const seq = useRef(0);

  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', theme);
    try {
      localStorage.setItem('erp.theme', theme);
    } catch {
      // ignore
    }
  }, [theme]);

  const toast = useCallback((text: string, tone: Toast['tone'] = 'ok') => {
    const id = ++seq.current;
    setToasts((ts) => [...ts.slice(-2), { id, tone, text }]);
    setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), tone === 'danger' ? 6000 : 3000);
  }, []);
  const [gate, setGate] = useState<ApprovalBlock | null>(null);
  const fail = useCallback(
    (err: unknown) => {
      const block = err instanceof ApiError ? (err.details?.approval as ApprovalBlock | undefined) : undefined;
      if (block) setGate(block);
      else if (err instanceof ApiError) toast(err.code === 'offline' ? t('error.offline') : err.message, 'danger');
      else toast(t('error.generic'), 'danger');
    },
    [toast],
  );

  const value = useMemo(() => ({ theme, setTheme: setThemeState, toast, fail }), [theme, toast, fail]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {gate ? <ApprovalGate block={gate} onClose={() => setGate(null)} toast={toast} fail={fail} /> : null}
      <div className="toasts" role="status" aria-live="polite">
        {toasts.map((x) => (
          <div key={x.id} className={`toast toast-${x.tone}`}>
            <Icon name={x.tone === 'danger' ? 'alert' : x.tone === 'info' ? 'bell' : 'checkCircle'} size={18} />
            <span>{x.text}</span>
          </div>
        ))}
      </div>
    </Ctx.Provider>
  );
}

export function useUi(): UiState {
  const v = useContext(Ctx);
  if (!v) throw new Error('useUi outside UiProvider');
  return v;
}

/** Re-renders every `ms` so timers on screen stay current. */
export function useNow(ms = 30_000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(id);
  }, [ms]);
  return now;
}
